'use client';

import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { getNonEcommerceMarketByCountry } from '@/core/constants/non-ecommerce-markets';
import useTranslations from '@/core/hooks/use-translations';
import Button from '@/ui/components/Button/Button';
import Dialog from '@/ui/components/Dialog/Dialog';
import Typography from '@/ui/components/Typography/Typography';
import { getCountryFlag } from '@/ui/utils/country/country';

import $ from './CountryDetectionModal.module.scss';

type CountryDetectionModalProps = {
  targetCountryCode: string;
  currentCountryCode: string;
  onClose: () => void;
  onContinueToTargetCountry: () => void;
};

const CountryDetectionModal: FC<CountryDetectionModalProps> = ({
  targetCountryCode,
  currentCountryCode,
  onClose,
  onContinueToTargetCountry,
}) => {
  const t = useTranslations();

  const countryDisplayNames = useMemo(
    () =>
      new Intl.DisplayNames('en', {
        type: 'region',
      }),
    [],
  );

  const getCountryName = useCallback(
    (countryCode: string): string => {
      const upperCode = countryCode.toUpperCase();
      const nonEcommerceMarket = getNonEcommerceMarketByCountry(upperCode);
      if (nonEcommerceMarket) {
        return nonEcommerceMarket.countryName;
      }

      return countryDisplayNames.of(upperCode) ?? upperCode;
    },
    [countryDisplayNames],
  );

  const targetCountryName = getCountryName(targetCountryCode);
  const currentCountryName = getCountryName(currentCountryCode);
  const targetCountryFlag = getCountryFlag(targetCountryCode);
  const currentCountryFlag = getCountryFlag(currentCountryCode);

  const handleStayInCurrentCountry = () => {
    onClose();
  };

  const title = t('web_countryDetection_modal_title');
  const description = t('web_countryDetection_modal_description');
  const continueText = t('web_countryDetection_modal_continue', {
    country: targetCountryName.toUpperCase(),
  });
  const stayText = t('web_countryDetection_modal_stay', {
    country: currentCountryName.toUpperCase(),
  });

  return (
    <Dialog variant="modal" withHeader onClose={onClose} className={$.modal}>
      <div className={$.content}>
        <Typography variant="h3" className={$.title}>
          {title}
        </Typography>
        <Typography variant="bodyMedium" className={$.description}>
          {description}
        </Typography>

        <div className={$.buttons}>
          <Button
            variant="primary"
            fullWidth
            onClick={onContinueToTargetCountry}
            className={$.continueButton}
          >
            <span className={$.flag} aria-hidden="true">
              {targetCountryFlag}
            </span>
            <span>{continueText}</span>
          </Button>

          <Button
            variant="secondary"
            fullWidth
            onClick={handleStayInCurrentCountry}
            className={$.stayButton}
          >
            <span className={$.flag} aria-hidden="true">
              {currentCountryFlag}
            </span>
            <span>{stayText}</span>
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default CountryDetectionModal;
